import type { ReactNode } from "react";

import { LegalMarkdown, legalSections } from "@/features/legal/components/legal-markdown";
import { PortalHead, PortalPage } from "@/features/portal/components/portal-chrome";
import { Eyebrow, sectionTitle } from "@/features/portal/components/portal-ui";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";
import { getLocale } from "@/paraglide/runtime.js";
import { LEGAL_DOCUMENTS_VERSION, isLegalEntityComplete } from "@complete-athlete/enums";

const lastUpdated = () =>
  new Intl.DateTimeFormat(getLocale(), { dateStyle: "long", timeZone: "UTC" }).format(new Date(LEGAL_DOCUMENTS_VERSION));

/** The page's head: which document this is and when it last changed. */
const LegalHead = ({ title, lede }: { title: string; lede: string }) => (
  <PortalHead>
    <div className="relative mx-auto max-w-[820px] px-1 pt-14 pb-4 text-center md:pt-20 md:pb-8">
      <Eyebrow>{m.legal_hub_title()}</Eyebrow>
      <h1 className={cn(sectionTitle, "mt-6 text-balance")}>{title}</h1>
      <p className="mx-auto mt-5 max-w-[52ch] text-[15px] text-pretty text-muted-foreground">{lede}</p>
    </div>
  </PortalHead>
);

/** Says so while the documents still carry blanks, rather than leaving a reader to wonder. */
const DraftNotice = () =>
  isLegalEntityComplete() ? null : (
    <p className="mb-8 rounded-2xl bg-muted px-4 py-3 text-[13px] leading-relaxed text-muted-foreground">
      {m.legal_draft_notice()}
    </p>
  );

/** One legal document: its contents on the side from `lg`, and the words in one card. */
export const LegalDocumentPage = ({ title, source }: { title: string; source: string }) => {
  const sections = legalSections(source);

  return (
    <PortalPage head={<LegalHead title={title} lede={m.auth_legal_last_updated({ date: lastUpdated() })} />}>
      <div className="mt-3 grid items-start gap-3 lg:grid-cols-[280px_minmax(0,1fr)]">
        {sections.length > 0 ? (
          <nav
            aria-label={m.legal_on_this_page()}
            className="sticky top-3 hidden rounded-3xl border border-border bg-background p-5 lg:block"
          >
            <p className="mb-2.5 text-[13px] font-medium text-muted-foreground">{m.legal_on_this_page()}</p>
            <ul className="space-y-1.5 text-[13px] leading-snug">
              {sections.map((section) => (
                <li key={section.id}>
                  <a href={`#${section.id}`} className="transition-colors hover:text-primary">
                    {section.title}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        ) : null}
        <article className="min-w-0 rounded-3xl border border-border bg-background px-5 py-8 md:px-12 md:py-12">
          <DraftNotice />
          <LegalMarkdown source={source} className="max-w-[68ch]" />
        </article>
      </div>
    </PortalPage>
  );
};

/** The page that lists the documents. */
export const LegalHubPage = ({ children }: { children: ReactNode }) => (
  <PortalPage head={<LegalHead title={m.legal_hub_title()} lede={m.legal_hub_lede()} />}>
    <div className="mt-3 grid items-start gap-3 lg:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)]">{children}</div>
  </PortalPage>
);
